
import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * NurtSip — Instant Variant Price V1
 *
 * Updates displayed variant prices immediately using
 * Shopify-rendered, market-localized pricing data.
 *
 * Horizon's native server update remains authoritative.
 */
class ProductPrice extends Component {
  #section;
  #picker;

  #onSelected = (event) => {
    if (!this.#picker || !this.isConnected) return;

    if (
      !(event.target instanceof Element) ||
      !this.#picker.contains(event.target)
    ) {
      return;
    }

    const script = this.#picker.querySelector(
      'script[data-nurtsip-variant-prices]'
    );

    if (!script?.textContent) return;

    let data;

    try {
      data = JSON.parse(script.textContent);
    } catch {
      return;
    }

    if (
      String(data.productId) !== String(this.dataset.productId)
    ) {
      return;
    }

    if (!Array.isArray(data.variants)) return;

    const selected = Array.from(
      this.#picker.querySelectorAll('fieldset, select')
    ).map((element) => {
      if (element instanceof HTMLSelectElement) {
        return element.value;
      }

      const checked = element.querySelector('input:checked');
      return checked?.value;
    });

    if (
      !selected.length ||
      selected.some((value) => value == null)
    ) {
      return;
    }

    const variant = data.variants.find(
      (item) =>
        Array.isArray(item.options) &&
        item.options.length === selected.length &&
        item.options.every(
          (value, index) => value === selected[index]
        )
    );

    if (!variant) return;

    const container = this.refs.priceContainer;
    if (!container) return;

    // Preserve native volume pricing behavior.
    if (this.refs.volumePricingNote) return;

    const existingRegular =
      container.querySelector('.price__regular');

    const existingSale =
      container.querySelector('.price__sale');

    if (!existingRegular || !existingSale) return;

    // Follow the currency display already used by Shopify.
    const currencyVisible = /\b[A-Z]{3}\b/.test(
      container.textContent || ''
    );

    const priceText = currencyVisible
      ? variant.priceWithCurrency
      : variant.priceFormatted;

    const compareText = currencyVisible
      ? variant.compareAtPriceWithCurrency
      : variant.compareAtPriceFormatted;

    if (!priceText) return;

    const isSale =
      Number(variant.compareAtPrice) >
      Number(variant.price);

    const regularPrice =
      existingRegular.querySelector('.price');

    const salePrice =
      existingSale.querySelector('.price-item--sale.price');

    const comparePrice =
      existingSale.querySelector('.compare-at-price');

    if (!regularPrice || !salePrice || !comparePrice) {
      return;
    }

    regularPrice.textContent = priceText;
    salePrice.textContent = priceText;

    comparePrice.textContent = isSale
      ? compareText
      : '';

    existingRegular.classList.toggle(
      'price__hidden',
      isSale
    );

    existingSale.classList.toggle(
      'price__hidden',
      !isSale
    );
  };

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest(
      '.shopify-section, dialog'
    );

    if (!this.#section) return;

    this.#section.addEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice
    );

    this.#picker = this.#section.querySelector(
      'variant-picker[ref="mainVariantPicker"]'
    );

    this.#picker?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onSelected
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#section?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice
    );

    this.#picker?.removeEventListener(
      ThemeEvents.variantSelected,
      this.#onSelected
    );
  }

  /**
   * Native Horizon server-side variant update.
   * This remains the final source of truth.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId =
        event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !==
        this.dataset.productId
    ) {
      return;
    }

    const {
      priceContainer,
      volumePricingNote
    } = this.refs;

    const newProductPrice =
      event.detail.data.html.querySelector(
        `product-price[data-block-id="${this.dataset.blockId}"]`
      );

    if (!newProductPrice) return;

    const newPrice = newProductPrice.querySelector(
      '[ref="priceContainer"]'
    );

    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    const newNote = newProductPrice.querySelector(
      '[ref="volumePricingNote"]'
    );

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      newPrice?.insertAdjacentElement(
        'afterend',
        newNote.cloneNode(true)
      );
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Preserve Shopify payment installments updates.
    const inputSelector =
      `#product-form-installment-${this.dataset.blockId} input[name="id"]`;

    const installmentsInput =
      this.querySelector(inputSelector);

    if (installmentsInput) {
      installmentsInput.value =
        event.detail.resource?.id ?? '';

      installmentsInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
